import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowUpDown, Check } from "lucide-react"

export type MasterDataSortValue =
  | "name_asc"
  | "name_desc"
  | "created_asc"
  | "created_desc"

const SORT_OPTIONS: {
  value: MasterDataSortValue
  labelKey: TranslationKeys
}[] = [
  { value: "name_asc", labelKey: "MC_MD_LIST_SORT_DEFAULT" },
  { value: "name_desc", labelKey: "MC_MD_LIST_SORT_NAME_DESC" },
  { value: "created_asc", labelKey: "MC_MD_LIST_SORT_CREATED_ASC" },
  { value: "created_desc", labelKey: "MC_MD_LIST_SORT_CREATED_DESC" },
]

/**
 * Sort control for the list header. Every option maps 1:1 to the master data
 * list endpoints' `sort` param — no UI-only mock options, since a selectable
 * option that doesn't change the list is misleading.
 */
export function MasterDataSort({
  value,
  onChange,
  defaultValue,
}: {
  value: MasterDataSortValue
  onChange: (value: MasterDataSortValue) => void
  /** This list's OWN resting sort, if it is not the first option.
   *
   *  The dot means "you have changed the sort from how this list normally
   *  arrives". Comparing against `SORT_OPTIONS[0]` assumed every list rests
   *  there; the inquiries panel rests on `created_desc`, so it drew the dot on
   *  first render, before the reviewer had touched anything — an indicator that
   *  is on by default indicates nothing. */
  defaultValue?: MasterDataSortValue
}) {
  const { translate } = useTranslation()
  const current =
    SORT_OPTIONS.find((option) => option.value === value) ?? SORT_OPTIONS[0]
  const isNonDefault = value !== (defaultValue ?? SORT_OPTIONS[0].value)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative size-8 shrink-0 border-grey4 bg-grey3 text-grey6 hover:text-white"
          aria-label={`${translate("MC_MD_LIST_SORT_LABEL")} ${translate(current.labelKey)}`}
          title={`${translate("MC_MD_LIST_SORT_LABEL")} ${translate(current.labelKey)}`}
        >
          <ArrowUpDown className="size-4" />
          {isNonDefault && (
            <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-yellow1" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {SORT_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
          >
            <Check
              className={cn(
                "size-3.5",
                option.value === value ? "text-yellow1" : "text-transparent",
              )}
            />
            {translate(option.labelKey)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
